import { ChangeDetectionStrategy, Component, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CreateMappingComponent } from '@shared/components/create-mapping/create-mapping.component';
import { EditMappingComponent } from '@shared/components/edit-mapping/edit-mapping.component';
import { FileuploadComponent } from '@shared/components/fileupload/fileupload.component';
import { GenerictableComponent } from '@shared/components/generictable/generictable.component';
import { SubMenuItem } from '@shared/components/header-sub-menu/header-sub-menu.component';
import { aciesDateFormat } from '@shared/helpers';
import { formatMappingToXLSX } from '@shared/mappers/mappingMapper';
import { GenericTableColumn, ISelectableData } from '@shared/models/generictablecolumn';
import { DocsService, IMappingBook } from '@shared/services/docs.service';
import { MappingValidatorService } from '@shared/services/filevalidators/mappingvalidator.service';
import { SelectSubMenuItem, SelectSubMenuItemManager } from '@shared/utils/SelectSubMenuItemManager';
import { actionDefintion } from '@shared/utils/SimpleActionManager';
import { finalize, map, mergeMap, Observable, Subject, take, takeUntil, tap } from 'rxjs';

import * as XLSX from 'xlsx';

@Component({
	selector: 'app-mappings',
	templateUrl: './mappings.component.html',
	styleUrls: ['./mappings.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class MappingsComponent {
	private destroy$: Subject<void> = new Subject<void>();

	public isLoading: boolean = false;
	public loadingSubject: Subject<boolean> = new Subject<boolean>();
	public showSearch: boolean = false;
	public mappingFilter: string = '';
	public totalCount: number;
	public pageSizes = [10, 20, 50, 100];
	public initialPageSize = 20;
	public countSubject$: Subject<number> = new Subject<number>();
	public count$ = this.countSubject$.asObservable();
	public selected: object[] = [];
	public mappingData: Observable<ISelectableData<IMappingBook>[]>;

	@ViewChild('tableRef') table: GenerictableComponent;

	public regRowActions: actionDefintion[] = [
		{
			title: 'docs.mappings.table.actions.edit',
			handler: 'editMappings',
		},
		{
			title: 'docs.mappings.table.actions.delete',
			handler: 'deleteMappings',
		},
	];

	public favoriteItems: SubMenuItem[] = [
		{
			click: () => this.importMappings(),
			icon: () => 'acies_upload',
			text: () => 'docs.mappings.import.button',
		},
		{
			click: () => this.openCreateMappingBook(),
			icon: () => 'acies_add_alt',
			tooltip: () => 'hello',
			text: () => 'docs.mappings.create.button',
		},
	];

	public regSelectActions: SelectSubMenuItem[] = [
		{
			text: () => 'docs.mappings.table.actions.export',
			click: (data) => this.exportMappings(data),
			icon: () => 'acies_download',
		},
	];

	public menuItems: SubMenuItem[] = [];

	constructor(
		public dialog: MatDialog,
		private injector: Injector,
		private docsService: DocsService,
	) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngOnInit(): void {
		this.loadData();

		this.setupActions();
	}

	selectActionManager: SelectSubMenuItemManager<ISelectableData<IMappingBook>>;

	ngAfterViewInit(): void {
		if (this?.table?.selectManager) {
			this.selectActionManager.setSelectManager(this.table.selectManager);
		}
	}

	setupActions(): void {
		this.selectActionManager = new SelectSubMenuItemManager<ISelectableData<IMappingBook>>(this.regSelectActions);
		this.menuItems.push(...this.selectActionManager.getSubMenuItems());
	}

	loadData(): void {
		this.isLoading = true;
		this.mappingData = this.docsService.getMappingsList().pipe(
			tap((data) => {
				this.isLoading = true;
				this.countSubject$.next(data.result.length);
			}),
			map((data) => {
				const items: ISelectableData<IMappingBook>[] = data.result.map((p) => {
					const tags = this.formatTagData(JSON.stringify(p.tags));
					return {
						id: tags,
						mappingDocument: p.mappingDocument,
						mappingsCount: Object.keys(p.mappingDocument['--systemspecificmappings--']).length,
						self: p.self,
						createdAt: aciesDateFormat(new Date(p.createdAt * 1000), true, false),
						updatedAt: p.updatedAt ? aciesDateFormat(new Date(p.updatedAt * 1000), true, false) : '',
						// the API counts seconds; kept in milliseconds, like every other list, for the sort. Null when
						// never updated, which Material sorts first
						createdAtEpoch: p.createdAt * 1000,
						updatedAtEpoch: p.updatedAt ? p.updatedAt * 1000 : null,
						tags,
					};
				});
				return items;
			}),
			finalize(() => {
				this.isLoading = false;
			}),
		);
	}

	public registerTableColumns: GenericTableColumn[] = [
		{ name: 'docs.mappings.table.tags', dataKey: 'tags' },
		{ name: 'docs.mappings.table.created', dataKey: 'createdAt', sortKey: 'createdAtEpoch' },
		{ name: 'docs.mappings.table.updated', dataKey: 'updatedAt', sortKey: 'updatedAtEpoch' },
		{ name: 'docs.mappings.table.items', dataKey: 'mappingsCount' },
	];

	handleRowMenuAction(data: any): void {
		switch (data['action'].handler) {
			case 'editMappings':
				{
					const self = data['data']['self'];
					void this.openEditMappingBook(self);
				}
				break;
			case 'deleteMappings':
				{
					const selfDelete = data['data']['self'];
					void this.openDeleteMappingBook(selfDelete);
				}
				break;
			case 'exportMappings':
				this.exportMappings(data.data as ISelectableData<IMappingBook>[]);
				break;
			default:
				break;
		}
	}

	handleReloadData(_: object): void {}

	handleSelected(_: object[]): void {}

	openEditMappingBook(self: string = null) {
		this.docsService
			.getMappingBook(self)
			.pipe(
				mergeMap((mappingBook) => {
					const dialogRef = this.dialog.open(EditMappingComponent, {
						disableClose: true,
						width: '650px',
						data: mappingBook.result,
					});
					return dialogRef.componentInstance.mappingEdited;
				}),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				this.loadData();
			});
	}

	openCreateMappingBook() {
		const dialogRef = this.dialog.open(CreateMappingComponent, {
			disableClose: true,
			width: '650px',
			data: null,
		});

		dialogRef.componentInstance.mappingCreated.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.loadData();
		});
	}

	openDeleteMappingBook(self: string) {
		this.docsService
			.deleteMappingBook(self, {
				type: 'book',
			})
			.pipe(take(1))
			.subscribe(() => {
				this.loadData();
			});
	}

	exportMappings(data: ISelectableData<IMappingBook>[]): void {
		const wb = formatMappingToXLSX(data);

		XLSX.writeFile(wb, 'mappings.xlsx');
	}

	importMappings() {
		const dialogRef = this.dialog.open(FileuploadComponent, {
			width: '450px',
			data: {
				headingKey: 'docs.mappings.import.title',
				subHeadingKey: 'docs.mappings.import.subtitle',
				confirmButtonKey: 'docs.mappings.import.button',
				notificationHeader: 'docs.mappings.import.notification.success.header',
				notificationContent: 'docs.mappings.import.notification.success.content',
				notificationErrorHeader: 'docs.mappings.import.notification.error.header',
				notificationErrorContent: 'docs.mappings.import.notification.error.content',
				notificationClientErrorHeader: 'docs.mappings.import.notification.clientError.header',
				notificationClientErrorContent: 'docs.mappings.import.notification.clientError.content',
				detailedClientErrorMessage: 'docs.mappings.import.detailed-client-error-message',
				allowedFileTypes: ['.xlsx'],
				validatorService: this.injector.get(MappingValidatorService),
				data: this.mappingData,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.loadData();
			});
	}

	formatTagData(tags: string): string {
		const data: any[] = JSON.parse(tags);
		let result = '';

		data.forEach((tag) => {
			result += `${tag.key}=${tag.value}, `;
		});

		if (result.length > 0) {
			result = result.substring(0, result.length - 2);
		}
		return result;
	}
}
